// Panel de la ventana de gracia de verificación de email (migr. 0037).
//
// Para QA: muestra si el gate está prendido, si la cuenta verificó, y cuánto
// queda de gracia — con un contador que corre en pantalla. Los botones acortan,
// vencen o quitan la ventana para poder recorrer los tres estados sin esperar
// horas reales.
//
// A diferencia del ActionsPanel, acá NO hay ritual de armar/confirmar: son
// acciones reversibles de un banco de pruebas y se usan en tandas (poner 2 min,
// ver vencer, volver a poner). El doble click ahí protege contra romper la
// cuenta sin querer; acá solo estorbaría.
//
// El contador descuenta desde `seconds_remaining`, que calcula Postgres: si el
// reloj de la máquina de QA está corrido, el número que ve sigue siendo el que
// usa el gate.

import { useEffect, useState } from 'react';
import { Hourglass, TimerOff, TimerReset, Eraser } from 'lucide-react';
import { runAction, type VerificationGrace } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { SectionPanel, EmptyHint } from './SectionPanel';
import { KVGrid } from './KVGrid';
import { StatusLed } from './StatusLed';

const PRESETS_MINUTES = [2, 15, 180] as const;

function formatRemaining(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
}

export function GracePanel({
  grace,
  updatedAt,
  onChanged,
  className = 'col-span-12 md:col-span-6 xl:col-span-4',
}: {
  grace: VerificationGrace | undefined;
  updatedAt?: number | undefined;
  /** Refrescar el snapshot tras mover la ventana. */
  onChanged: () => void;
  /** Ancho en la grilla del dashboard. Lo decide el caller, no el panel. */
  className?: string;
}) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);

  // Re-sincroniza con cada respuesta del backend y descuenta localmente entre
  // polls (el poll live es de 5 s; el contador tiene que verse fluido).
  const serverRemaining = grace?.seconds_remaining ?? null;
  useEffect(() => {
    setRemaining(serverRemaining);
    if (serverRemaining === null) return;
    const id = setInterval(() => {
      setRemaining((r) => (r === null ? null : Math.max(0, r - 1)));
    }, 1000);
    return () => clearInterval(id);
  }, [serverRemaining]);

  async function fire(action: 'grace_set' | 'grace_expire' | 'grace_clear', minutes?: number) {
    setBusy(true);
    setErr(null);
    try {
      await runAction(action, minutes !== undefined ? { graceMinutes: minutes } : undefined);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'error');
    } finally {
      setBusy(false);
    }
  }

  const active = (remaining ?? 0) > 0;
  // Lo que decide el gate ahora: verificado pasa siempre; sin verificar, pasa
  // solo si la ventana está viva (o si el partner no exige verificación).
  const wouldPass = !grace?.require_email_verification || grace.email_verified || active;

  return (
    <SectionPanel
      title={t('t.grace')}
      data={grace}
      updatedAt={updatedAt}
      className={className}
    >
      {grace ? (
        <>
          <KVGrid
            items={[
              {
                k: t('grace.kv.gate'),
                v: (
                  <StatusLed
                    tone={grace.require_email_verification ? 'warning' : 'idle'}
                    label={grace.require_email_verification ? t('val.on') : t('val.off')}
                  />
                ),
              },
              {
                k: t('grace.kv.verified'),
                v: (
                  <StatusLed
                    tone={grace.email_verified ? 'success' : 'warning'}
                    label={grace.email_verified ? t('val.yes') : t('val.no')}
                  />
                ),
              },
              { k: t('grace.kv.planHours'), v: grace.plan_grace_hours ?? '—' },
              {
                k: t('grace.kv.remaining'),
                strong: true,
                v:
                  remaining === null ? (
                    t('grace.none')
                  ) : (
                    <span className={`tabular-nums ${active ? 'text-status-success' : 'text-status-error'}`}>
                      {active ? formatRemaining(remaining) : t('grace.expired')}
                    </span>
                  ),
              },
              {
                k: t('grace.kv.verdict'),
                v: (
                  <StatusLed
                    tone={wouldPass ? 'success' : 'error'}
                    label={wouldPass ? t('grace.wouldPass') : t('grace.wouldFail')}
                  />
                ),
              },
            ]}
          />

          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-surface-border pt-2">
            {PRESETS_MINUTES.map((m) => (
              <button
                key={m}
                type="button"
                disabled={busy}
                title={t('grace.set.hint')}
                onClick={() => void fire('grace_set', m)}
                className="flex items-center gap-1 rounded border border-surface-border-strong px-2 py-1 text-[11px] uppercase tracking-wider text-ink-muted transition-colors hover:border-brand hover:text-brand disabled:opacity-40"
              >
                <TimerReset className="h-3.5 w-3.5" aria-hidden="true" />
                {m < 60 ? t('grace.presetMin', { n: m }) : t('grace.presetHour', { n: m / 60 })}
              </button>
            ))}
            <button
              type="button"
              disabled={busy}
              title={t('grace.expire.hint')}
              onClick={() => void fire('grace_expire')}
              className="flex items-center gap-1 rounded border border-surface-border-strong px-2 py-1 text-[11px] uppercase tracking-wider text-ink-muted transition-colors hover:border-status-warning hover:text-status-warning disabled:opacity-40"
            >
              <TimerOff className="h-3.5 w-3.5" aria-hidden="true" />
              {t('grace.expire')}
            </button>
            <button
              type="button"
              disabled={busy}
              title={t('grace.clear.hint')}
              onClick={() => void fire('grace_clear')}
              className="flex items-center gap-1 rounded border border-surface-border-strong px-2 py-1 text-[11px] uppercase tracking-wider text-ink-muted transition-colors hover:border-status-warning hover:text-status-warning disabled:opacity-40"
            >
              <Eraser className="h-3.5 w-3.5" aria-hidden="true" />
              {t('grace.clear')}
            </button>
          </div>

          <p className="mt-1.5 flex items-start gap-1 text-[10px] leading-tight text-ink-subtle">
            <Hourglass className="mt-[1px] h-3 w-3 shrink-0" aria-hidden="true" />
            {t('grace.help')}
          </p>

          {err && (
            <p role="alert" className="mt-1 text-[11px] text-status-error">
              {err}
            </p>
          )}
        </>
      ) : (
        <EmptyHint>{t('grace.empty')}</EmptyHint>
      )}
    </SectionPanel>
  );
}
